import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { ISettlementItemData } from 'types/order/fees/ISectionData';
import { ISettlementAndFeeCategoryDefinitionData } from 'types/order/fees/ISettlementAndFeeCategoryDefinition';
import { Currency, FlexRow, Grid, Header3Text, Header5Text } from 'ui-toolkit';
import { StyledFeeSection, StyledFeeSectionHeader } from './FeeSections.Styled';
import { IFeeSection } from './organizeFees';
import { FeeSectionTable } from './table/FeeSectionTable';
import { Validation } from './validation/Validation';
import { EditContainer } from './edit/EditContainer';
import { clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { LinkTW } from 'tailwindComponents/link/LinkTW';
import { useParams } from 'react-router-dom';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';

export interface IPurchaseFeeSectionProps {
  leftSection: IFeeSection;
  rightSection: IFeeSection;
  subLabel?: string | undefined;
}

const getSequenceForItem = (item: ISettlementItemData, options: ISettlementAndFeeCategoryDefinitionData[]) => {
  return (
    options.find(
      (t) => t.statementSettlementAndFeeCategoryDefinitionId === item.statementSettlementAndFeeCategoryDefinitionId
    )?.feeSeq ?? 0
  );
};

const SubLabel = styled(Header5Text)`
  padding: ${(props) => props.theme.spacing.roomyContentPaddingValue}px;
`;

export const PurchaseFeeSection: React.FC<IPurchaseFeeSectionProps> = ({ leftSection, rightSection, subLabel }) => {
  const hasEditPermission = useHasPermissions([ePermissions.CdItemsV3Edit]);
  const { orderId, feeId } = useParams<{ orderId: string; feeId: string }>();
  const leftTop = leftSection.settlementItems.filter((t) => getSequenceForItem(t, leftSection.feeDefinitions) < 1000);
  const leftBottom = leftSection.settlementItems.filter(
    (t) => getSequenceForItem(t, leftSection.feeDefinitions) >= 1000
  );
  const rightTop = rightSection.settlementItems.filter(
    (t) => getSequenceForItem(t, rightSection.feeDefinitions) < 1000
  );
  const rightBottom = rightSection.settlementItems.filter(
    (t) => getSequenceForItem(t, rightSection.feeDefinitions) >= 1000
  );
  const negativeCashToBuyer =
    leftSection.sectionDefinition.sectionDescription === 'Cash To Close From Buyer' &&
    (leftSection.sectionData?.sectionPropertyTotals[eFeePropertyNames.Amount] ?? 0) < 0;
  const cashToCloseConversion = negativeCashToBuyer
    ? 'Cash To Close To Buyer'
    : leftSection.sectionDefinition.sectionDescription;

  const negativeCashToSeller =
    rightSection.sectionDefinition.sectionDescription === 'Cash To Seller' &&
    (rightSection.sectionData?.sectionPropertyTotals[eFeePropertyNames.Amount] ?? 0) < 0;
  const cashToSellerConversion = negativeCashToSeller
    ? 'Cash From Seller'
    : rightSection.sectionDefinition.sectionDescription;

  const showLeftSideSheet = feeId === leftSection.sectionDefinition.statementSectionDefinitionId;
  const showRightSideSheet = feeId === rightSection.sectionDefinition.statementSectionDefinitionId;

  return (
    <Grid columnCount={2}>
      <StyledFeeSection>
        <StyledFeeSectionHeader>
          <Header3Text>
            {leftSection.sectionDefinition.sectionLetter && `${leftSection.sectionDefinition.sectionLetter}. `}
            {cashToCloseConversion}
          </Header3Text>
          <Header3Text>
            {!!leftSection.sectionData?.sectionPropertyTotals[eFeePropertyNames.Amount] && (
              <Currency value={Math.abs(leftSection.sectionData?.sectionPropertyTotals[eFeePropertyNames.Amount])} />
            )}
          </Header3Text>
          {!!leftSection.options.length && hasEditPermission && (
            <LinkTW
              isButton
              to={clientUrls.urlFor.feesTabEdit(
                orderId,
                'k-n',
                leftSection.sectionDefinition.statementSectionDefinitionId
              )}
              data-test-id={'add-left'}
            >
              <FlexRow gap={'spacing2'}>
                <FontAwesomeIcon icon={faPlus} />
                Add
              </FlexRow>
            </LinkTW>
          )}
        </StyledFeeSectionHeader>
        {showLeftSideSheet && (
          <EditContainer
            layout={'Single Column'}
            section={leftSection.sectionData}
            sectionDefinition={leftSection.sectionDefinition}
            orderId={orderId}
          />
        )}
        {leftSection.sectionData && (
          <Validation
            sections={[leftSection.sectionData]}
            sectionDefinitions={[leftSection.sectionDefinition]}
            className={'grid p-4'}
          />
        )}
        <FeeSectionTable
          feeSection={leftSection}
          items={leftTop}
          blankRows={leftTop.length < rightTop.length ? rightTop.length - leftTop.length : 0}
          layout="Single Column"
        />
        {!!subLabel && (
          <>
            <SubLabel>{subLabel}</SubLabel>
            <FeeSectionTable feeSection={leftSection} items={leftBottom} layout="Single Column" />
          </>
        )}
      </StyledFeeSection>
      <StyledFeeSection>
        <StyledFeeSectionHeader>
          <Header3Text>
            {rightSection.sectionDefinition.sectionLetter && `${rightSection.sectionDefinition.sectionLetter}. `}
            {cashToSellerConversion}
          </Header3Text>
          <Header3Text>
            {!!rightSection.sectionData?.sectionPropertyTotals[eFeePropertyNames.Amount] && (
              <Currency value={Math.abs(rightSection.sectionData?.sectionPropertyTotals[eFeePropertyNames.Amount])} />
            )}
          </Header3Text>
          {!!rightSection.options.length && hasEditPermission && (
            <LinkTW
              isButton
              to={clientUrls.urlFor.feesTabEdit(
                orderId,
                'k-n',
                rightSection.sectionDefinition.statementSectionDefinitionId
              )}
            >
              <FlexRow gap={'spacing2'}>
                <FontAwesomeIcon icon={faPlus} />
                Add
              </FlexRow>
            </LinkTW>
          )}
        </StyledFeeSectionHeader>
        {showRightSideSheet && (
          <EditContainer
            layout={'Single Column'}
            section={rightSection.sectionData}
            sectionDefinition={rightSection.sectionDefinition}
            orderId={orderId}
          />
        )}
        {rightSection.sectionData && (
          <Validation
            sections={[rightSection.sectionData]}
            sectionDefinitions={[rightSection.sectionDefinition]}
            className={'grid p-4'}
          />
        )}
        <FeeSectionTable
          feeSection={rightSection}
          items={rightTop}
          blankRows={leftTop.length > rightTop.length ? leftTop.length - rightTop.length : 0}
          layout="Single Column"
        />
        {!!subLabel && (
          <>
            <SubLabel>{subLabel}</SubLabel>
            <FeeSectionTable feeSection={rightSection} items={rightBottom} layout="Single Column" />
          </>
        )}
      </StyledFeeSection>
    </Grid>
  );
};
